import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type AssetFormValues, assetSchema } from "@/lib/schemas";
import { formatNumberWithCommas, parseNumberWithCommas } from "@/lib/utils";
import assetService from "@/services/AssetService";
import type { Asset, AssetType } from "@/types/asset";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import {
  type Control,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form";

interface EditAssetFormProps {
  asset: Asset;
  onCancel: () => void;
  onUpdate: (asset: Asset) => void;
}

// Map display names to AssetType enum values
const PROPERTY_TYPES: Record<string, AssetType> = {
  "Single Family": "single_family",
  Condominium: "condominium",
  "Multi-Family": "multi_family",
  Commercial: "commercial",
  Industrial: "industrial",
  Agricultural: "agricultural",
  "Vacant Land": "vacant_land",
  "Mixed-Use": "mixed_use",
  "Public/Institutional": "public_institutional",
  Recreational: "recreational",
  Transportation: "transportation",
};

// Helper function to format date for HTML date input
const formatDateForInput = (dateValue: string | null | undefined): string => {
  if (!dateValue) return "";

  try {
    // If it's already a date string, parse it
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";

    // Return in YYYY-MM-DD format for HTML date input
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Type for the form control to avoid 'any'
type FormControlType = Control<AssetFormValues>;

export default function EditAssetForm({
  asset,
  onCancel,
  onUpdate,
}: EditAssetFormProps) {
  // Track formatted display values
  const [formattedValues, setFormattedValues] = useState({
    purchase_price: formatNumberWithCommas(asset.purchase_price),
    closing_costs: formatNumberWithCommas(asset.closing_costs),
    market_value: formatNumberWithCommas(asset.market_value),
    market_rent: formatNumberWithCommas(asset.market_rent),
    loan_amount: formatNumberWithCommas(asset.loan_amount),
    down_payment: formatNumberWithCommas(asset.down_payment),
    area_square_feet: formatNumberWithCommas(asset.area_square_feet),
    lot_size_square_feet: formatNumberWithCommas(asset.lot_size_square_feet),
  });

  // Use a type assertion for the resolver to work around the type incompatibility
  const resolver = zodResolver(
    assetSchema
  ) as unknown as Resolver<AssetFormValues>;

  const form = useForm<AssetFormValues>({
    resolver,
    defaultValues: {
      name: asset.name || "",
      address: asset.address || "",
      asset_type: asset.asset_type as AssetType,
      entity_id: asset.entity_id,
      user_role: asset.user_role || "O",
      self_managed: asset.self_managed !== false, // default to true if not specified
      management_platform: asset.management_platform || "none",
      purchase_date: asset.purchase_date || null,
      purchase_price: asset.purchase_price || null,
      closing_costs: asset.closing_costs || null,
      market_value: asset.market_value || null,
      market_rent: asset.market_rent || null,
      loan_amount: asset.loan_amount || null,
      loan_due_date: asset.loan_due_date || null,
      down_payment: asset.down_payment || null,
      beds: asset.beds || null,
      baths: asset.baths || null,
      year_built: asset.year_built || null,
      stories: asset.stories || null,
      units: asset.units || 1,
      area_square_feet: asset.area_square_feet || null,
      lot_size_square_feet: asset.lot_size_square_feet || null,
      land_percentage: asset.land_percentage || 0,
      investment_status: asset.investment_status || "active",
      notes: asset.notes || null,
    },
  });

  const [generalError, setGeneralError] = useState<string | null>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  const handleNumberChange = (
    field: keyof typeof formattedValues,
    value: string
  ) => {
    // Parse the input value to a number
    const parsedValue = parseNumberWithCommas(value);

    // Update the formatted display value with commas
    setFormattedValues((prev) => ({
      ...prev,
      [field]: formatNumberWithCommas(parsedValue),
    }));

    // Update the form value with the parsed number
    form.setValue(field as keyof AssetFormValues, parsedValue);
  };

  const onSubmit: SubmitHandler<AssetFormValues> = async (formValues) => {
    setGeneralError(null);
    try {
      // Log the raw form values
      console.log("Raw form values:", formValues);

      // Create a fresh object with the form values - Zod conversion has already happened
      const submissionData: Partial<Asset> = {
        ...formValues,
        // Ensure these fields don't have undefined values
        address: formValues.address || undefined,
        notes: formValues.notes || undefined,
        purchase_date: formValues.purchase_date || undefined,
        // Handle null values that should be numbers or undefined
        units: formValues.units || 1,
        land_percentage: formValues.land_percentage || 0,
      };

      console.log("Final submission data:", submissionData);

      const updatedAsset = await assetService.updateAsset(
        asset.id,
        submissionData
      );
      onUpdate(updatedAsset);
    } catch (error: any) {
      console.error("Error updating asset:", error);
      // Try to extract validation errors from backend
      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        let firstField: string | null = null;
        for (const [field, message] of Object.entries(errors)) {
          form.setError(field as keyof AssetFormValues, {
            message: String(message),
          });
          if (!firstField) firstField = field;
        }
        setGeneralError("Please fix the errors below before saving.");
        setTimeout(() => {
          errorSummaryRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else if (error?.response?.data?.message) {
        setGeneralError(error.response.data.message);
        setTimeout(() => {
          errorSummaryRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        setGeneralError("Failed to update asset. Please try again.");
        setTimeout(() => {
          errorSummaryRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  };

  // Type-cast the control once to reuse it across all form fields
  const controlWithType = form.control as unknown as FormControlType;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Error summary at the top of the form */}
        {(generalError || Object.keys(form.formState.errors).length > 0) && (
          <div
            ref={errorSummaryRef}
            className="bg-red-100 border border-red-300 text-red-800 rounded-md px-4 py-3 mb-4"
            role="alert"
            tabIndex={-1}
          >
            {generalError || "Please fix the errors below before saving."}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold mb-4">Property Details</h2>
          </div>

          {/* Name */}
          <FormField
            control={controlWithType}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Address */}
          <FormField
            control={controlWithType}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Property Type */}
          <FormField
            control={controlWithType}
            name="asset_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(PROPERTY_TYPES).map(([name, value]) => (
                      <SelectItem key={value} value={value}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Units - only show for multi-family properties */}
          {form.watch("asset_type") === "multi_family" && (
            <FormField
              control={controlWithType}
              name="units"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Number of Units</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value
                            ? Number.parseInt(e.target.value, 10)
                            : 1
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Year Built */}
          <FormField
            control={controlWithType}
            name="year_built"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Year Built</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value || ""}
                    onChange={(e) =>
                      field.onChange(e.target.valueAsNumber || null)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Beds */}
          <FormField
            control={controlWithType}
            name="beds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bedrooms</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value || ""}
                    onChange={(e) =>
                      field.onChange(e.target.valueAsNumber || null)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Baths */}
          <FormField
            control={controlWithType}
            name="baths"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bathrooms</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.1"
                    {...field}
                    value={field.value || ""}
                    onChange={(e) =>
                      field.onChange(e.target.valueAsNumber || null)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Square Feet */}
          <FormField
            control={controlWithType}
            name="area_square_feet"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Square Feet</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.area_square_feet}
                    onChange={(e) =>
                      handleNumberChange("area_square_feet", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Lot Size */}
          <FormField
            control={controlWithType}
            name="lot_size_square_feet"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lot Size (sq ft)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.lot_size_square_feet}
                    onChange={(e) =>
                      handleNumberChange("lot_size_square_feet", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold mb-4 mt-6">
              Financial Details
            </h2>
          </div>

          {/* Purchase Price */}
          <FormField
            control={controlWithType}
            name="purchase_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Purchase Price ($)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.purchase_price}
                    onChange={(e) =>
                      handleNumberChange("purchase_price", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Closing Costs */}
          <FormField
            control={controlWithType}
            name="closing_costs"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Closing Costs ($)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.closing_costs}
                    onChange={(e) =>
                      handleNumberChange("closing_costs", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Purchase Date */}
          <FormField
            control={controlWithType}
            name="purchase_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Purchase Date</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    {...field}
                    value={formatDateForInput(field.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Market Value */}
          <FormField
            control={controlWithType}
            name="market_value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Market Value ($)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.market_value}
                    onChange={(e) =>
                      handleNumberChange("market_value", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Market Rent */}
          <FormField
            control={controlWithType}
            name="market_rent"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Monthly Market Rent ($)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.market_rent}
                    onChange={(e) =>
                      handleNumberChange("market_rent", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Loan Amount */}
          <FormField
            control={controlWithType}
            name="loan_amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Loan Amount ($)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.loan_amount}
                    onChange={(e) =>
                      handleNumberChange("loan_amount", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Loan Due Date - Only show if there's a loan amount */}
          {form.watch("loan_amount") &&
            Number(form.watch("loan_amount")) > 0 && (
              <FormField
                control={controlWithType}
                name="loan_due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Loan Due Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={formatDateForInput(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

          {/* Down Payment */}
          <FormField
            control={controlWithType}
            name="down_payment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Down Payment ($)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formattedValues.down_payment}
                    onChange={(e) =>
                      handleNumberChange("down_payment", e.target.value)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
